import QRCode from "qrcode";
import QrScanner from "qr-scanner";

import { makeIpiSignDoc } from "../probes/keplr-send/tx.mjs";
import {
  baseToDisplay,
  displayToBase,
  requireMatchingKeplrKey,
  validateAddress,
} from "../probes/keplr-send/core.mjs";
import {
  QR_PAYLOAD_TYPES,
  decodeQrEnvelope,
  decodeQrFrame,
  reassembleQrFrames,
} from "../transport/wallet-qr-transport.mjs";
import {
  checkoutResultQr,
  makeCheckoutSignDoc,
  parseCheckoutRequest,
  validateLiveCheckout,
} from "./checkout.mjs";
import {
  CHAIN,
  CHECKOUT_FEE,
  KEPLR_CHAIN_INFO,
  SEND_FEE,
} from "./config.mjs";
import { ReceiptStore, confirmedReceipt } from "./receipts.mjs";
import { queryCheckout, queryProductOwner } from "./query.mjs";
import {
  queryAccount,
  queryBalance,
  signAndBroadcastVerified,
  waitForInclusion,
} from "./tx.mjs";

const root = document.getElementById("real-wallet-root");

if (root) {
  const receipts = new ReceiptStore(window.localStorage);
  const multipart = new Map();
  let session = null;
  let pendingSend = null;
  let pendingCheckout = null;
  let scanner = null;
  let screenWakeLock = null;

  root.innerHTML = `
    <div class="real-heading">
      <div>
        <span class="eyebrow">REAL KEPLR WALLET · PUBLIC TESTNET</span>
        <h2>Send, receive and Checkout V2</h2>
        <p>Buyer keys stay in Keplr. A success receipt appears only after code-0 inclusion and live state verification.</p>
      </div>
      <div class="real-connect">
        <button class="button" id="real-connect" type="button">Connect Keplr</button>
        <button class="button ghost" id="real-disconnect" type="button" disabled>Disconnect</button>
        <span id="real-status" role="status" aria-live="polite">Keplr is not connected.</span>
      </div>
    </div>
    <div class="real-account">
      <div><span>Network</span><strong>${CHAIN.chainId}</strong></div>
      <div><span>Buyer address</span><strong id="real-address">Not connected</strong></div>
      <div><span>Live balance</span><strong id="real-balance">—</strong></div>
      <button class="button ghost" id="real-refresh" type="button" disabled>Refresh live state</button>
    </div>
    <div class="real-grid">
      <section class="real-panel">
        <h3>Receive IPI</h3>
        <p>Share the complete connected IPI address.</p>
        <canvas id="real-receive-qr" aria-label="Receive address QR"></canvas>
        <code id="real-receive-address">Connect Keplr to create the QR.</code>
      </section>
      <section class="real-panel">
        <h3>Send IPI</h3>
        <label for="real-send-recipient">Recipient</label>
        <input id="real-send-recipient" autocomplete="off" spellcheck="false" disabled>
        <label for="real-send-amount">Amount in IPI</label>
        <input id="real-send-amount" inputmode="decimal" value="0.001" disabled>
        <p class="real-note">Fee: ${baseToDisplay(SEND_FEE.amount)} IPI · ${SEND_FEE.gasLimit} gas</p>
        <button class="button alt" id="real-send-review" type="button" disabled>Review normal Send</button>
        <div id="real-send-summary" class="real-summary">No transfer prepared.</div>
        <button class="button" id="real-send-confirm" type="button" disabled>Sign and broadcast Send</button>
      </section>
    </div>
    <section class="real-panel real-checkout">
      <div class="real-checkout-head">
        <div>
          <h3>Checkout V2 request</h3>
          <p>Only the real terminal-signed <code>${CHAIN.checkoutTypeUrl}</code> can be approved.</p>
        </div>
        <div class="real-actions">
          <button class="button alt" id="real-scan" type="button" disabled>Scan terminal QR</button>
          <button class="button ghost" id="real-stop-scan" type="button" disabled>Stop camera</button>
          <label class="button ghost real-file">Import QR image<input id="real-import-image" type="file" accept="image/*" disabled></label>
        </div>
      </div>
      <div id="real-camera-shell" class="real-camera-shell" hidden>
        <video id="real-camera" playsinline muted></video>
      </div>
      <label for="real-checkout-input">Terminal V2 QR envelope</label>
      <textarea id="real-checkout-input" spellcheck="false" disabled placeholder="Paste a complete IPIQR3 request envelope"></textarea>
      <button class="button alt" id="real-paste-request" type="button" disabled>Paste request from clipboard</button>
      <button class="button ghost" id="real-checkout-validate" type="button" disabled>Validate terminal request and live records</button>
      <div id="real-checkout-summary" class="real-summary">No checkout request loaded.</div>
      <button class="button" id="real-checkout-confirm" type="button" disabled>Sign and broadcast Checkout V2</button>
      <div id="real-result-qr" class="real-result-qr"></div>
    </section>
    <section class="real-panel">
      <h3>Confirmed transaction history</h3>
      <div id="real-history" class="real-history"></div>
    </section>`;

  const element = (id) => document.getElementById(`real-${id}`);
  const elements = Object.fromEntries([
    "connect", "disconnect", "status", "address", "balance", "refresh",
    "receive-qr", "receive-address", "send-recipient", "send-amount",
    "send-review", "send-summary", "send-confirm", "scan", "stop-scan",
    "import-image", "camera-shell", "camera", "checkout-input",
    "paste-request", "checkout-validate", "checkout-summary", "checkout-confirm",
    "result-qr", "history",
  ].map((name) => [name, element(name)]));
  const primaryConnect = document.querySelector("[data-primary-wallet-connect='keplr']");
  const primaryFeedback = document.getElementById("hero-keplr-feedback");

  function escape(value) {
    return String(value).replace(/[&<>'"]/g, (character) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
    })[character]);
  }

  function pastedAddress(value) {
    return String(value)
      .trim()
      .replace(/^`|`$/g, "")
      .replace(/^ipi:/i, "")
      .replace(/[\u200b-\u200d\uFEFF]/g, "")
      .trim();
  }

  function status(message, kind = "") {
    elements.status.textContent = message;
    elements.status.dataset.kind = kind;
    if (primaryFeedback) {
      primaryFeedback.hidden = false;
      primaryFeedback.className = `wallet-feedback hero-wallet-feedback ${kind}`;
      primaryFeedback.textContent = message;
    }
  }

  function enabled(value) {
    for (const name of [
      "refresh", "send-recipient", "send-amount", "send-review", "scan",
      "import-image", "checkout-input", "paste-request", "checkout-validate",
    ]) elements[name].disabled = !value;
    elements.disconnect.disabled = !value;
  }

  async function waitForKeplr(timeoutMs = 5_000) {
    const deadline = Date.now() + timeoutMs;
    while (!window.keplr && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (!window.keplr) {
      throw new Error(
        "Keplr provider was not detected. Open wallet.ipi.io in Keplr Mobile’s Browser and reload.",
      );
    }
    return window.keplr;
  }

  async function refreshBalance() {
    if (!session) throw new Error("connect Keplr first");
    session.balance = await queryBalance(fetch, session.address);
    elements.balance.textContent = `${baseToDisplay(session.balance)} IPI`;
    return session.balance;
  }

  async function connect() {
    elements.connect.disabled = true;
    status("Waiting for Keplr network authorization…");
    try {
      const keplr = await waitForKeplr();
      await keplr.experimentalSuggestChain(KEPLR_CHAIN_INFO);
      await keplr.enable(CHAIN.chainId);
      const key = requireMatchingKeplrKey(await keplr.getKey(CHAIN.chainId));
      session = {
        keplr,
        address: key.bech32Address,
        publicKey: key.pubKey,
        balance: "0",
      };
      elements.address.textContent = session.address;
      elements["receive-address"].textContent = session.address;
      await QRCode.toCanvas(elements["receive-qr"], session.address, {
        width: 220,
        margin: 2,
        color: { dark: "#101a32", light: "#ffffff" },
      });
      await refreshBalance();
      enabled(true);
      status("Keplr connected. Every signature remains inside Keplr.", "success");
      if (elements["checkout-input"].value) await validateImported();
    } finally {
      elements.connect.disabled = false;
    }
  }

  function disconnect() {
    stopScan();
    session = null;
    pendingSend = null;
    pendingCheckout = null;
    enabled(false);
    elements.address.textContent = "Not connected";
    elements.balance.textContent = "—";
    elements["receive-address"].textContent = "Connect Keplr to create the QR.";
    elements["receive-qr"].getContext("2d").clearRect(
      0,
      0,
      elements["receive-qr"].width,
      elements["receive-qr"].height,
    );
    elements["send-confirm"].disabled = true;
    elements["checkout-confirm"].disabled = true;
    status("Local wallet session disconnected.");
  }

  function renderHistory() {
    const history = receipts.list();
    elements.history.innerHTML = history.length ? history.map((receipt) => `
      <article>
        <strong>${receipt.kind === "checkout" ? "Checkout V2" : "Normal Send"} · block ${escape(receipt.height)}</strong>
        <code>${escape(receipt.txHash)}</code>
        <span>code ${receipt.code} · ${escape(receipt.confirmedAt)}</span>
        ${receipt.kind === "checkout"
          ? `<span>${escape(receipt.checkoutId)} · owner ${escape(receipt.ownerAfter)}</span>`
          : `<span>${escape(baseToDisplay(receipt.amount))} IPI → ${escape(receipt.recipient)}</span>`}
      </article>`).join("") : "<p>No confirmed real transactions stored locally.</p>";
  }

  function reviewSend() {
    if (!session) throw new Error("connect Keplr first");
    const recipient = validateAddress(pastedAddress(elements["send-recipient"].value));
    elements["send-recipient"].value = recipient;
    if (recipient === session.address) throw new Error("recipient must differ from sender");
    const amount = displayToBase(elements["send-amount"].value);
    if (BigInt(amount) + BigInt(SEND_FEE.amount) > BigInt(session.balance)) {
      throw new Error("amount plus fee exceeds live balance");
    }
    pendingSend = Object.freeze({ recipient, amount });
    elements["send-summary"].innerHTML = `<dl>
      <div><dt>Network</dt><dd>${CHAIN.chainId}</dd></div>
      <div><dt>From</dt><dd>${escape(session.address)}</dd></div>
      <div><dt>To</dt><dd>${escape(recipient)}</dd></div>
      <div><dt>Amount</dt><dd>${escape(baseToDisplay(amount))} IPI</dd></div>
      <div><dt>Fee</dt><dd>${escape(baseToDisplay(SEND_FEE.amount))} IPI</dd></div>
    </dl>`;
    elements["send-confirm"].disabled = false;
    status("Normal Send reviewed. Use Sign and broadcast when ready.");
  }

  async function executeSend() {
    if (!session || !pendingSend) throw new Error("review a normal Send first");
    elements["send-confirm"].disabled = true;
    const senderBefore = await refreshBalance();
    const recipientBefore = await queryBalance(fetch, pendingSend.recipient);
    if (BigInt(pendingSend.amount) + BigInt(SEND_FEE.amount) > BigInt(senderBefore)) {
      throw new Error("amount plus fee now exceeds live balance");
    }
    const account = await queryAccount(fetch, session.address);
    const signDoc = makeIpiSignDoc({
      fromAddress: session.address,
      toAddress: pendingSend.recipient,
      amount: pendingSend.amount,
      pubkey: session.publicKey,
      ...account,
    });
    status("Inspect and approve the normal IPI transfer in Keplr.");
    const txHash = await signAndBroadcastVerified({
      keplr: session.keplr,
      fetchFn: fetch,
      address: session.address,
      publicKey: session.publicKey,
      signDoc,
    });
    status(`Send ${txHash} broadcast after local signature verification. Waiting for inclusion…`);
    const inclusion = await waitForInclusion(fetch, txHash);
    const senderAfter = await refreshBalance();
    const recipientAfter = await queryBalance(fetch, pendingSend.recipient);
    if (
      BigInt(senderBefore) - BigInt(senderAfter)
        !== BigInt(pendingSend.amount) + BigInt(SEND_FEE.amount)
      || BigInt(recipientAfter) - BigInt(recipientBefore) !== BigInt(pendingSend.amount)
    ) throw new Error("included Send balance deltas do not match reviewed amount and fee");
    const receipt = confirmedReceipt(inclusion, {
      kind: "send",
      sender: session.address,
      recipient: pendingSend.recipient,
      amount: pendingSend.amount,
      fee: SEND_FEE.amount,
      senderBefore,
      senderAfter,
      recipientBefore,
      recipientAfter,
      signatureVerified: true,
    });
    receipts.add(receipt);
    pendingSend = null;
    elements["send-summary"].textContent = `Confirmed ${receipt.txHash} at height ${receipt.height}; code 0.`;
    renderHistory();
    status("Normal Send confirmed with exact balance deltas.", "success");
  }

  function unwrapEnvelope(text) {
    const decoded = decodeQrEnvelope(text.trim(), QR_PAYLOAD_TYPES.REQUEST);
    if (decoded.envelope.protocol_version !== 2) throw new Error("terminal request protocol is not V2");
    return decoded.payload_text;
  }

  async function validateImported() {
    if (!session) throw new Error("connect Keplr before validating checkout");
    elements["checkout-confirm"].disabled = true;
    const message = parseCheckoutRequest(unwrapEnvelope(elements["checkout-input"].value), {
      buyerAddress: session.address,
    });
    const live = await validateLiveCheckout(message, {
      fetchFn: fetch,
      balance: await refreshBalance(),
    });
    pendingCheckout = Object.freeze({ message, live });
    elements["checkout-summary"].innerHTML = `<dl>
      <div><dt>Type</dt><dd>${CHAIN.checkoutTypeUrl}</dd></div>
      <div><dt>Network</dt><dd>${escape(message.chain_id)}</dd></div>
      <div><dt>Buyer</dt><dd>${escape(message.buyer)}</dd></div>
      <div><dt>Merchant</dt><dd>${escape(message.merchant_id)} · ${escape(message.merchant_address)}</dd></div>
      <div><dt>Terminal</dt><dd>${escape(message.terminal_id)} · ${escape(message.terminal_key_id)}</dd></div>
      <div><dt>Checkout</dt><dd>${escape(message.checkout_id)}</dd></div>
      <div><dt>Request</dt><dd>${escape(message.request_id)}</dd></div>
      <div><dt>Request hash</dt><dd>${escape(message.request_hash)}</dd></div>
      <div><dt>Created / expires</dt><dd>${new Date(message.created_at_ms).toISOString()} / ${new Date(message.expires_at_ms).toISOString()}</dd></div>
      <div><dt>Card</dt><dd>${escape(message.ipi_card_commitment)} · config ${escape(message.card_verification_config_id)} · counter ${message.card_read_counter}</dd></div>
      <div><dt>Card proof</dt><dd>${escape(message.card_proof_identity)}</dd></div>
      <div><dt>Nonce</dt><dd>${escape(message.nonce)}</dd></div>
      <div><dt>Terminal signature</dt><dd>verified against live terminal record</dd></div>
      <div><dt>Total</dt><dd>${escape(baseToDisplay(message.total_amount))} IPI + ${escape(baseToDisplay(CHECKOUT_FEE.amount))} IPI fee</dd></div>
    </dl>
    <div class="real-items">${message.products.map((item) => `<article>
      <strong>${escape(item.product_id)}</strong>
      <span>UID ${escape(item.tag_uid)}</span>
      <span>${item.quantity} × ${escape(baseToDisplay(item.unit_price))} IPI = ${escape(baseToDisplay(item.line_total))} IPI</span>
      <span>Current/expected owner: ${escape(item.expected_owner)}</span>
    </article>`).join("")}</div>`;
    elements["checkout-confirm"].disabled = false;
    status("Terminal signature, expiry, products, owner and buyer balance verified.", "success");
  }

  async function pasteRequest() {
    if (!navigator.clipboard?.readText) throw new Error("clipboard access is unavailable");
    const text = await navigator.clipboard.readText();
    if (!text.trim()) throw new Error("clipboard does not contain a terminal request");
    elements["checkout-input"].value = text.trim();
    await validateImported();
  }

  async function executeCheckout() {
    if (!session || !pendingCheckout) throw new Error("validate a Checkout V2 request first");
    elements["checkout-confirm"].disabled = true;
    const { message } = pendingCheckout;
    const buyerBefore = await refreshBalance();
    const merchantBefore = await queryBalance(fetch, message.merchant_address);
    await validateLiveCheckout(message, { fetchFn: fetch, balance: buyerBefore });
    const account = await queryAccount(fetch, session.address);
    const signDoc = makeCheckoutSignDoc({
      message,
      pubkey: session.publicKey,
      ...account,
    });
    status("Inspect every Checkout V2 field in Keplr, then approve the direct signature.");
    const txHash = await signAndBroadcastVerified({
      keplr: session.keplr,
      fetchFn: fetch,
      address: session.address,
      publicKey: session.publicKey,
      signDoc,
    });
    status(`Checkout ${txHash} broadcast after local signature verification. Waiting for inclusion…`);
    const inclusion = await waitForInclusion(fetch, txHash);
    const buyerAfter = await refreshBalance();
    const merchantAfter = await queryBalance(fetch, message.merchant_address);
    const checkout = await queryCheckout(fetch, message.checkout_id);
    if (
      checkout?.status !== "SETTLED"
      || checkout.buyer !== session.address
      || checkout.request_hash !== message.request_hash
      || checkout.total_amount !== message.total_amount
    ) throw new Error("included checkout record does not match reviewed request");
    if (
      BigInt(buyerBefore) - BigInt(buyerAfter)
        !== BigInt(message.total_amount) + BigInt(CHECKOUT_FEE.amount)
      || BigInt(merchantAfter) - BigInt(merchantBefore) !== BigInt(message.total_amount)
    ) throw new Error("checkout balance deltas do not match reviewed total and fee");
    for (const item of message.products) {
      const product = await queryProductOwner(fetch, item.product_id);
      if (product?.current_owner !== session.address) {
        throw new Error(`product ${item.product_id} ownership did not move to buyer`);
      }
    }
    const receipt = confirmedReceipt(inclusion, {
      kind: "checkout",
      checkoutId: message.checkout_id,
      requestId: message.request_id,
      requestHash: message.request_hash,
      buyer: session.address,
      merchant: message.merchant_address,
      amount: message.total_amount,
      fee: CHECKOUT_FEE.amount,
      buyerBefore,
      buyerAfter,
      merchantBefore,
      merchantAfter,
      productIds: message.products.map((item) => item.product_id),
      ownerAfter: session.address,
      signatureVerified: true,
      terminalSignatureVerified: true,
    });
    receipts.add(receipt);
    pendingCheckout = null;
    elements["checkout-summary"].textContent =
      `SETTLED ${receipt.txHash} at height ${receipt.height}; balances and ownership verified.`;
    const qrText = checkoutResultQr(receipt);
    elements["result-qr"].innerHTML = "<canvas aria-label=\"Compact checkout result QR\"></canvas><code></code>";
    await QRCode.toCanvas(elements["result-qr"].querySelector("canvas"), qrText, {
      width: 260,
      margin: 2,
      errorCorrectionLevel: "M",
    });
    elements["result-qr"].querySelector("code").textContent = qrText;
    renderHistory();
    status("Checkout V2 settled with code 0 and all live state checks passed.", "success");
  }

  async function ingest(text) {
    if (text.startsWith("IPIQR2P:") || text.startsWith("IPIQR3P:")) {
      const frame = decodeQrFrame(text);
      const frames = multipart.get(frame.message_id) ?? new Map();
      frames.set(frame.part_index, text);
      multipart.set(frame.message_id, frames);
      status(`Scanned terminal QR part ${frames.size}/${frame.total_parts}.`);
      if (frames.size < frame.total_parts) return;
      elements["checkout-input"].value = reassembleQrFrames([...frames.values()]);
      multipart.delete(frame.message_id);
    } else {
      elements["checkout-input"].value = text;
    }
    stopScan();
    await validateImported();
  }

  async function startScan() {
    stopScan();
    elements["camera-shell"].hidden = false;
    scanner = new QrScanner(elements.camera, (result) => {
      if (result?.data) ingest(result.data).catch((error) => status(error.message, "error"));
    }, {
      preferredCamera: "environment",
      returnDetailedScanResult: true,
      highlightScanRegion: true,
      highlightCodeOutline: true,
      maxScansPerSecond: 20,
    });
    await scanner.start();
    await requestScreenWakeLock();
    elements.scan.disabled = true;
    elements["stop-scan"].disabled = false;
    status("Camera active. Keep the terminal QR inside the frame.");
  }

  async function requestScreenWakeLock() {
    if (!navigator.wakeLock || screenWakeLock) return;
    try {
      const wakeLock = await navigator.wakeLock.request("screen");
      screenWakeLock = wakeLock;
      wakeLock.addEventListener("release", () => {
        if (screenWakeLock === wakeLock) screenWakeLock = null;
      }, { once: true });
    } catch {
      screenWakeLock = null;
    }
  }

  function stopScan() {
    scanner?.stop();
    scanner?.destroy();
    scanner = null;
    const wakeLock = screenWakeLock;
    screenWakeLock = null;
    wakeLock?.release().catch(() => {});
    elements["camera-shell"].hidden = true;
    elements.scan.disabled = !session;
    elements["stop-scan"].disabled = true;
  }

  async function importImage(file) {
    if (!file) return;
    const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true });
    await ingest(result.data);
  }

  async function action(callback, button) {
    button.disabled = true;
    try {
      await callback();
    } catch (error) {
      status(error.message, "error");
    } finally {
      if (session && button !== elements["checkout-confirm"] && button !== elements["send-confirm"]) {
        button.disabled = false;
      }
    }
  }

  elements.connect.addEventListener("click", () => action(connect, elements.connect));
  primaryConnect?.addEventListener("click", () => {
    action(connect, primaryConnect).finally(() => {
      primaryConnect.disabled = false;
    });
  });
  elements.disconnect.addEventListener("click", disconnect);
  elements.refresh.addEventListener("click", () => action(refreshBalance, elements.refresh));
  elements["send-review"].addEventListener("click", () => {
    try { reviewSend(); } catch (error) { status(error.message, "error"); }
  });
  elements["send-confirm"].addEventListener("click", () => action(executeSend, elements["send-confirm"]));
  elements["checkout-validate"].addEventListener("click", () => action(
    validateImported,
    elements["checkout-validate"],
  ));
  elements["paste-request"].addEventListener("click", () => action(
    pasteRequest,
    elements["paste-request"],
  ));
  elements["checkout-confirm"].addEventListener("click", () => action(
    executeCheckout,
    elements["checkout-confirm"],
  ));
  elements.scan.addEventListener("click", () => action(startScan, elements.scan));
  elements["stop-scan"].addEventListener("click", stopScan);
  elements["import-image"].addEventListener("change", () => action(
    () => importImage(elements["import-image"].files?.[0]),
    elements["import-image"],
  ));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && scanner) {
      requestScreenWakeLock();
    }
  });

  enabled(false);
  renderHistory();
}
